// The screens' side of the outside's contract (contract.ts): when a host draws
// the frame itself (`extensions.shellHost`, a native app's web view), the menu
// and the shown screen's title and buttons go to it as data, and what it sends
// back (an entry, a button, back) runs here.
//
// Promise:
// - Without a host nothing is sent and nothing changes: the web draws its own
//   frame as before.
// - A screen hands its title and buttons with `useShellHeader`; the last one
//   mounted wins, and when it goes the one before it shows again (null when
//   none is left). One of something over the whole screen (`over`) wins over
//   the screens' under it, and the app's way back steps back inside it. A
//   screen that hands none still gets its `PanelHeader` title as a plain
//   header.
// - The app's way back goes back a page; on the page the window started on
//   (the app opened again where it was left) a detail's goes up to its list.
// - Floating parts the host draws go to it as data (floats.tsx).
// - A page's own buttons on a phone's Dock go to a host that draws the Dock
//   (`useShellDockTool`), in the order they were mounted; the list is sent
//   again only when its data changes.
// - A button pressed runs the handler the screen gave last, never a stale one.
// - The menu is sent again only when it changed (badges included), with the
//   Dock's places as the person keeps them on this device (`dock`).
import { useRouter, useRouterState } from "@tanstack/react-router";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMeApp } from "../../app/context";
import type { NavItem } from "../../app/screen-part";
import { useDeviceSetting } from "../../device/device-setting";
import { navigateFromNotice } from "../../device/os-notices";
import {
  useCreateFolder,
  useDeleteFolder,
  useMoveFavorite,
  useRenameFolder,
} from "../../me/favorites";
import type { MenuItem } from "../../ui/menu";
import { dockPrefsSetting } from "../dock-prefs";
import { hasPageBefore, isOverlayEntry } from "../page-history";
import type { DockItem } from "../phone";
import {
  ComposerContext,
  createComposerStore,
  setHostKeyboard,
} from "./composer-host";
import {
  SHELL_CONTRACT_VERSION,
  type ShellAction,
  type ShellBadge,
  type ShellChip,
  type ShellHeader,
  type ShellHostNotices,
  type ShellMenu,
  type ShellMenuSeparator,
  type ShellMessageFromHost,
  type ShellMessageToHost,
  type ShellOngoing,
  type ShellPanel,
} from "./contract";
import { createFloatStore, FloatContext } from "./floats";
import { useShellMenu } from "./source";

/** What draws the frame outside the screens (a native app). */
export interface ShellHost {
  /**
   * The screens are embedded: the host draws the menu and the header, so the
   * screens draw neither their sidebar nor their header row.
   */
  embedded: boolean;
  /**
   * The floating parts it draws (`ShellFloatKind`s, from `ShellHostInfo`):
   * the web hands them as data instead of drawing them. Absent: none.
   */
  floats?: ReadonlySet<string>;
  /**
   * It frames the screens as a phone's at any width (`ShellHostInfo.phone`):
   * they keep the phone's frame wider than a phone.
   */
  phone?: boolean;
  /**
   * It shows notifications of its own (`ShellHostInfo.notices`): its
   * installation and device. Absent: it shows none.
   */
  notices?: ShellHostNotices;
  /** It has settings of its own (`ShellHostInfo.appSettings`), opened with `app-settings`. */
  appSettings?: boolean;
  /**
   * It shows pictures and videos large itself (`ShellHostInfo.viewer`): the
   * viewers hand them over with `viewer`.
   */
  viewer?: boolean;
  post(message: ShellMessageToHost): void;
  /** Returns the unsubscribe. */
  subscribe(listener: (message: ShellMessageFromHost) => void): () => void;
}

/** A header button as a screen hands it: the data and what pressing does. */
export interface ShellActionInput extends Omit<ShellAction, "items" | "panel"> {
  /** `text`: the words an item with a `prompt` asked for. */
  onPress?(text?: string): void;
  items?: (ShellActionInput | ShellMenuSeparator)[];
  /** The panel the host draws (`ShellAction.panel`), its button run here. */
  panel?: Omit<ShellPanel, "button"> & {
    button?: NonNullable<ShellPanel["button"]> & { onPress(): void };
  };
  /**
   * How the web's own header draws it (`DetailHeader`), given whether the row
   * is narrow; null for nothing there. Never sent: the outside draws the data.
   * Without it the web draws the data's plain button.
   */
  render?(narrow: boolean): ReactNode;
  /** Only the web's (drawn by `render`, never sent): a part's button with no data. */
  webOnly?: boolean;
}

/** A chip beside the title as a screen hands it (see `ShellActionInput`). */
export interface ShellChipInput extends Omit<ShellChip, "items"> {
  onPress?(): void;
  items?: (ShellActionInput | ShellMenuSeparator)[];
  render?(narrow: boolean): ReactNode;
}

export interface ShellHeaderInput {
  title: string;
  back?: boolean;
  /**
   * With `back`: where the app's way back goes when this window has no page
   * before (it started on this one). Given by `ShellUpContext` (a detail's list).
   */
  up?: string;
  /**
   * Of something over the whole screen (a dialog filling a phone's screen):
   * the app shows no way back to the page under it, and its own way back
   * (a swipe) runs `onBack`, or nothing.
   */
  over?: boolean;
  /**
   * With `over`: a step back inside it, the app's way back. `slid`: it has
   * slid out already (the app's edge swipe moved it off): no move of its own.
   */
  onBack?(how?: { slid: boolean }): void;
  /**
   * With `over`: what slides as a screen pushed from the right (the app's
   * edge swipe moves it with the finger, contract 26).
   */
  layer?(): HTMLElement | null | undefined;
  chips?: ShellChipInput[];
  note?: string;
  actions?: ShellActionInput[];
}

type Slot = "screen" | "title";

interface HeaderStore {
  set(
    slot: Slot,
    owner: object,
    input: { current: ShellHeaderInput | null } | null,
  ): void;
  press(id: string, text?: string): void;
  /**
   * The app's way back: true when the header shown is over the screen (its
   * own step back ran, or nothing), false to go back a page.
   */
  back(): boolean;
  /** Where the shown screen's way back goes with no page before (`up`). */
  up(): string | null;
  /** The host's edge swipe moves what lies over the screen this far right. */
  drag(x: number): void;
  /** The swipe let go: back (it slides off, then its way back) or not (it returns). */
  dragEnd(back: boolean): void;
  /**
   * Moving to another page whose data is still coming: the page left stays
   * on the screen meanwhile, but its buttons are not offered (the host's bar
   * is already the next page's; a press there opened the page left's menu,
   * gone the moment the next page showed).
   */
  moving(on: boolean): void;
}

const OutsideContext = createContext<HeaderStore | null>(null);

/**
 * The screens' place to go up to (a detail's list): a header with a way back
 * handed inside it takes it as its `up`.
 */
export const ShellUpContext = createContext<string | null>(null);

/** Whether the screens are embedded in a host that draws the frame. */
export function useShellEmbedded(): boolean {
  return useMeApp().extensions.shellHost?.embedded ?? false;
}

const isSeparator = (
  a: ShellActionInput | ShellMenuSeparator,
): a is ShellMenuSeparator => "type" in a && a.type === "separator";

function toAction(a: ShellActionInput): ShellAction {
  const { onPress: _, render: __, webOnly: ___, items, panel, ...rest } = a;
  return {
    ...rest,
    ...(items
      ? { items: items.map((i) => (isSeparator(i) ? i : toAction(i))) }
      : {}),
    ...(panel ? { panel: toPanel(panel) } : {}),
  };
}

function toPanel(panel: NonNullable<ShellActionInput["panel"]>): ShellPanel {
  const { button, ...rest } = panel;
  if (!button) return rest;
  const { onPress: _, ...shown } = button;
  return { ...rest, button: shown };
}

/** The id an over header's way back is pressed with (`ShellHeader.backAction`). */
const BACK_ACTION = "over-back";

const toHeader = (input: ShellHeaderInput): ShellHeader => ({
  version: SHELL_CONTRACT_VERSION,
  title: input.title,
  ...(input.over
    ? { over: true, ...(input.onBack ? { backAction: BACK_ACTION } : {}) }
    : input.back
      ? { back: true }
      : {}),
  ...(input.chips?.length
    ? {
        chips: input.chips.map(({ onPress: _, render: __, items, ...chip }) =>
          items
            ? {
                ...chip,
                items: items.map((i) => (isSeparator(i) ? i : toAction(i))),
              }
            : chip,
        ),
      }
    : {}),
  ...(input.note ? { note: input.note } : {}),
  actions: (input.actions ?? []).filter((a) => !a.webOnly).map(toAction),
});

function findAction(
  list: readonly (ShellActionInput | ShellMenuSeparator)[],
  id: string,
): ShellActionInput | undefined {
  for (const a of list) {
    // A web-only button is never sent, so a press is never its: a part's
    // dialog shares its id with the menu's item that opens it.
    if (isSeparator(a) || a.webOnly) continue;
    if (a.id === id) return a;
    if (a.panel?.button?.id === id) return { ...a.panel.button };
    const inner = a.items && findAction(a.items, id);
    if (inner) return inner;
  }
  return undefined;
}

function createHeaderStore(host: ShellHost): HeaderStore {
  // Mounted order: the last header over the screen wins (a dialog filling it,
  // whichever mounted first), else the last screen's, else the last plain title.
  let entries: {
    slot: Slot;
    owner: object;
    input: { current: ShellHeaderInput | null };
  }[] = [];
  let sent: string | undefined;
  let moving = false;
  const last = (slot: Slot) =>
    entries.findLast((e) => e.slot === slot && e.input.current)?.input
      .current ?? null;
  const current = () =>
    entries.findLast((e) => e.input.current?.over)?.input.current ??
    last("screen") ??
    last("title");
  const send = () => {
    const input = current();
    const header = input
      ? moving && !input.over
        ? { ...toHeader({ ...input, chips: [] }), actions: [] }
        : toHeader(input)
      : null;
    const json = JSON.stringify(header);
    if (json === sent) return;
    sent = json;
    host.post({ version: SHELL_CONTRACT_VERSION, type: "header", header });
  };
  return {
    set(slot, owner, input) {
      const at = entries.findIndex((e) => e.owner === owner);
      if (input && at >= 0) entries[at] = { slot, owner, input };
      else if (input) entries.push({ slot, owner, input });
      else if (at >= 0) entries = entries.filter((e) => e.owner !== owner);
      else return;
      send();
    },
    moving(on) {
      if (moving === on) return;
      moving = on;
      send();
    },
    press(id, text) {
      if (moving) return;
      const header = current();
      if (header?.over && id === BACK_ACTION) return header.onBack?.();
      const chip = header?.chips?.find((c) => c.id === id);
      if (chip) return chip.onPress?.();
      const item =
        findAction(header?.actions ?? [], id) ??
        (header?.chips ?? []).reduce<ShellActionInput | undefined>(
          (found, c) => found ?? findAction(c.items ?? [], id),
          undefined,
        );
      item?.onPress?.(text);
    },
    back() {
      const header = current();
      if (!header?.over) return false;
      header.onBack?.();
      return true;
    },
    up() {
      const header = current();
      return header?.back ? (header.up ?? null) : null;
    },
    drag(x) {
      const header = current();
      const el = header?.over ? header.layer?.() : null;
      if (!el) return;
      for (const a of el.getAnimations?.() ?? []) a.cancel();
      el.style.transform = `translateX(${x}px)`;
    },
    dragEnd(back) {
      const header = current();
      if (!header?.over) return;
      const el = header.layer?.();
      if (!el?.animate) {
        if (el) el.style.transform = "";
        if (back) header.onBack?.();
        return;
      }
      const from = el.style.transform || "none";
      el.style.transform = "";
      const move = el.animate(
        [
          { transform: from },
          { transform: back ? "translateX(100%)" : "none" },
        ],
        {
          duration: SLIDE_MS,
          easing: back
            ? "cubic-bezier(0.4, 0, 0.6, 1)"
            : "cubic-bezier(0.2, 0.8, 0.2, 1)",
          fill: back ? "forwards" : "none",
        },
      );
      if (back) move.onfinish = () => header.onBack?.({ slid: true });
    },
  };
}

/** How long something over the screen slides (as a pushed screen's). */
const SLIDE_MS = 200;

/** A page's button on the Dock, with its place among them (lowest first). */
type ToolInput = ShellActionInput & { order?: number };

interface ToolStore {
  set(owner: object, input: { current: ToolInput | null } | null): void;
  /** Runs the button (or an item of its menu) with this id; false when none has it. */
  press(id: string): boolean;
}

function createToolStore(host: ShellHost): ToolStore {
  let entries: {
    owner: object;
    input: { current: ToolInput | null };
  }[] = [];
  let sent = "[]";
  // By their order (unset: last), then as mounted (`sort` is stable).
  const shown = () =>
    entries
      .flatMap((e) => (e.input.current ? [e.input.current] : []))
      .sort(
        (a, b) =>
          (a.order ?? Number.POSITIVE_INFINITY) -
          (b.order ?? Number.POSITIVE_INFINITY),
      );
  const send = () => {
    const tools = shown().map(({ order: _, ...a }) => toAction(a));
    const json = JSON.stringify(tools);
    if (json === sent) return;
    sent = json;
    host.post({ version: SHELL_CONTRACT_VERSION, type: "tools", tools });
  };
  return {
    set(owner, input) {
      const at = entries.findIndex((e) => e.owner === owner);
      if (input && at >= 0) entries[at] = { owner, input };
      else if (input) entries.push({ owner, input });
      else if (at >= 0) entries = entries.filter((e) => e.owner !== owner);
      else return;
      send();
    },
    press(id) {
      const tool = findAction(shown(), id);
      tool?.onPress?.();
      return tool !== undefined;
    },
  };
}

const ToolContext = createContext<ToolStore | null>(null);

/**
 * Hands one of a page's buttons on a phone's Dock to the host that draws the
 * Dock (null: none). Pressing it there runs its `onPress`.
 */
export function useShellDockTool(input: ToolInput | null): void {
  const store = useContext(ToolContext);
  const [owner] = useState(() => ({}));
  const latest = useRef(input);
  latest.current = input;
  const key = input ? JSON.stringify([input.order, toAction(input)]) : null;
  // Tells the host (an outside system) when the button's data changes; the
  // handlers are read from `latest` when pressed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for the data
  useEffect(() => {
    store?.set(owner, latest.current ? latest : null);
  }, [store, owner, key]);
  useEffect(
    () => () => {
      store?.set(owner, null);
    },
    [store, owner],
  );
}

/** The data of a header input, for telling when it changed. */
const headerKey = (input: ShellHeaderInput | null) =>
  input ? JSON.stringify(toHeader(input)) : null;

function useHeaderSlot(slot: Slot, input: ShellHeaderInput | null) {
  const store = useContext(OutsideContext);
  const up = useContext(ShellUpContext);
  const [owner] = useState(() => ({}));
  const latest = useRef(input);
  latest.current = input?.back && up && !input.up ? { ...input, up } : input;
  const key = headerKey(input);
  // Tells the host (an outside system) when the header's data changes; the
  // handlers are read from `latest` when pressed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for the data
  useEffect(() => {
    if (!store) return;
    store.set(slot, owner, latest.current ? latest : null);
  }, [store, slot, owner, key]);
  useEffect(
    () => () => {
      store?.set(slot, owner, null);
    },
    [store, slot, owner],
  );
}

/**
 * Hands the shown screen's title and buttons to the host (when there is one;
 * otherwise does nothing). Pressing a button there runs its `onPress`.
 */
export function useShellHeader(input: ShellHeaderInput | null): void {
  useHeaderSlot("screen", input);
}

/** `PanelHeader`'s plain title, for screens that hand no header of their own. */
export function useShellTitle(title: string | null): void {
  useHeaderSlot("title", title === null ? null : { title });
}

/** A web menu's items as header buttons' items (the "..." menu). */
export function menuActions(
  items: readonly (MenuItem | "separator")[],
): (ShellActionInput | ShellMenuSeparator)[] {
  return items.map((i) =>
    i === "separator"
      ? { type: "separator" }
      : {
          id: i.id,
          label: i.label,
          ...(i.danger ? { danger: true } : {}),
          ...(i.disabled ? { disabled: true } : {}),
          onPress: i.onSelect,
        },
  );
}

/**
 * Around the screens when the host draws the frame: sends the menu, the
 * location and the header, and runs what the host sends back.
 */
export function ShellOutside({
  host,
  children,
}: {
  host: ShellHost;
  children: ReactNode;
}) {
  const router = useRouter();
  const store = useMemo(() => createHeaderStore(host), [host]);
  const floats = useMemo(() => createFloatStore(host), [host]);
  const tools = useMemo(() => createToolStore(host), [host]);
  const composer = useMemo(() => createComposerStore(host), [host]);
  const shell = useShellMenu();
  const select = useRef(shell.select);
  select.current = shell.select;
  // Where the screen shown is: while another page's data is coming, the
  // page left still shows, and the host's bar stays its (a way back drawn
  // early lay over the list's title).
  const path = useRouterState({
    select: (s) => (s.resolvedLocation ?? s.location).pathname,
  });
  // Another page's data is coming while the page left still shows.
  const moving = useRouterState({
    select: (s) =>
      s.status === "pending" &&
      s.resolvedLocation !== undefined &&
      s.resolvedLocation.pathname !== s.location.pathname,
  });
  // The host is an outside system: these effects talk to it, they fetch nothing.
  // The buttons go before the place does: the host's bar for the next page
  // never shows the page left's.
  useEffect(() => store.moving(moving), [store, moving]);
  useEffect(() => {
    host.post({ version: SHELL_CONTRACT_VERSION, type: "location", path });
  }, [host, path]);
  useEffect(
    () =>
      host.subscribe((msg) => {
        if (msg.type === "action") {
          if (!floats.press(msg.id) && !tools.press(msg.id))
            store.press(msg.id, msg.text);
        } else if (msg.type === "over-drag") store.drag(msg.x);
        else if (msg.type === "over-drag-end") store.dragEnd(msg.back);
        else if (msg.type === "back") {
          if (store.back()) return;
          // Started on this page (the app opened again where it was left):
          // nothing before it, so up to the screen's list.
          const history = router.history;
          const up = store.up();
          if (up && !isOverlayEntry(history) && !hasPageBefore(history))
            void router.navigate({ to: up });
          else history.back();
        } else if (msg.type === "composer") composer.receive(msg);
        else if (msg.type === "keyboard") setHostKeyboard(msg.height);
        else if (msg.type === "open") navigateFromNotice(router, msg.path);
        else if (msg.type === "select") select.current(msg.id);
      }),
    [host, store, floats, tools, composer, router],
  );
  return (
    <OutsideContext.Provider value={store}>
      <FloatContext.Provider value={floats}>
        <ToolContext.Provider value={tools}>
          <ComposerContext.Provider value={composer}>
            {children}
            <MenuPublisher host={host} />
            <FavoriteChanges host={host} />
          </ComposerContext.Provider>
        </ToolContext.Provider>
      </FloatContext.Provider>
    </OutsideContext.Provider>
  );
}

/**
 * Runs the changes a host makes to a place's favorites it draws (`favorite`),
 * with the same mutations as the web's own favorites; the menu then goes out
 * again with them.
 */
function FavoriteChanges({ host }: { host: ShellHost }) {
  const place = useShellMenu().menu.place?.sidebar?.favorites?.place ?? null;
  return place ? <PlaceFavoriteChanges host={host} place={place} /> : null;
}

function PlaceFavoriteChanges({
  host,
  place,
}: {
  host: ShellHost;
  place: string;
}) {
  const create = useCreateFolder(place);
  const rename = useRenameFolder(place);
  const remove = useDeleteFolder(place);
  const move = useMoveFavorite(place);
  const run = useRef({ create, rename, remove, move });
  run.current = { create, rename, remove, move };
  // The host is an outside system: this listens to it, it fetches nothing.
  useEffect(
    () =>
      host.subscribe((msg) => {
        if (msg.type !== "favorite" || msg.place !== place) return;
        const r = run.current;
        if (msg.op === "move")
          r.move.mutate({
            entry: msg.entry,
            folderId: msg.folder,
            before: msg.before,
          });
        else if (msg.op === "folder-new") r.create.mutate({ name: msg.name });
        else if (msg.op === "folder-rename")
          r.rename.mutate({ id: msg.id, name: msg.name });
        else r.remove.mutate({ id: msg.id });
      }),
    [host, place],
  );
  return null;
}

type BadgeReport = (id: string, badge: ShellBadge | null) => void;

/** Sends the frame's menu, with the entries' badges as data. */
function MenuPublisher({ host }: { host: ShellHost }) {
  const app = useMeApp();
  const { menu, place } = useShellMenu();
  const [badges, setBadges] = useState<Readonly<Record<string, ShellBadge>>>(
    {},
  );
  const report = useCallback<BadgeReport>(
    (id, badge) =>
      setBadges((prev) => {
        if (JSON.stringify(prev[id] ?? null) === JSON.stringify(badge))
          return prev;
        const next = { ...prev };
        if (badge) next[id] = badge;
        else delete next[id];
        return next;
      }),
    [],
  );
  // The Dock's places as kept on this device, once the person set them.
  // What goes on (the parts' `useOngoingData`), for a place's own sidebar.
  const [ongoing, setOngoing] = useState<
    Readonly<Record<string, ShellOngoing>>
  >({});
  const reportOngoing = useCallback(
    (id: string, data: ShellOngoing | null) =>
      setOngoing((prev) => {
        if (JSON.stringify(prev[id] ?? null) === JSON.stringify(data))
          return prev;
        const next = { ...prev };
        if (data) next[id] = data;
        else delete next[id];
        return next;
      }),
    [],
  );
  const [dockPrefs] = useDeviceSetting(dockPrefsSetting);
  const kept = dockPrefs.order.length > 0 || dockPrefs.hidden.length > 0;
  const withBadges: ShellMenu = {
    ...menu,
    entries: menu.entries.map((e) => {
      const badge = e.type === "item" ? badges[e.id] : undefined;
      return badge ? { ...e, badge } : e;
    }),
    ...(kept
      ? { dock: { order: [...dockPrefs.order], hidden: [...dockPrefs.hidden] } }
      : {}),
    ...(menu.place?.sidebar
      ? {
          place: {
            ...menu.place,
            sidebar: {
              ...menu.place.sidebar,
              ongoing: Object.values(ongoing),
            },
          },
        }
      : {}),
  };
  const json = JSON.stringify(withBadges);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `json` stands for the menu
  useEffect(() => {
    host.post({
      version: SHELL_CONTRACT_VERSION,
      type: "menu",
      menu: withBadges,
    });
  }, [host, json]);
  const placeProbes = place?.items.map((i) => (
    <BadgeProbe key={i.id} id={`place:${i.id}`} item={i} report={report} />
  ));
  return (
    <>
      {app.parts.map((p) =>
        p.useOngoingData ? (
          <OngoingProbe
            key={p.id}
            id={p.id}
            use={p.useOngoingData}
            report={reportOngoing}
          />
        ) : null,
      )}
      {app.nav.map((n) => (
        <BadgeProbe key={n.to} id={`me:${n.to}`} item={n} report={report} />
      ))}
      {place?.Scope ? <place.Scope>{placeProbes}</place.Scope> : placeProbes}
    </>
  );
}

/** Reads one part's ongoing row as data. */
function OngoingProbe({
  id,
  use,
  report,
}: {
  id: string;
  use: () => ShellOngoing | null;
  report: (id: string, data: ShellOngoing | null) => void;
}) {
  const data = use();
  const json = JSON.stringify(data);
  // frontend-development: not a fetch; hands the part's data to the menu
  // biome-ignore lint/correctness/useExhaustiveDependencies: `json` stands for the data
  useEffect(() => {
    report(id, data);
  }, [id, json, report]);
  // frontend-development: not a fetch; takes it away with the part
  useEffect(() => () => report(id, null), [id, report]);
  return null;
}

/** Reads one entry's badge as data (a plain dot when it has only a drawn one). */
function BadgeProbe({
  id,
  item,
  report,
}: {
  id: string;
  item: Pick<NavItem | DockItem, "useBadge" | "useBadgeData">;
  report: BadgeReport;
}) {
  return item.useBadgeData ? (
    <DataProbe id={id} use={item.useBadgeData} report={report} />
  ) : item.useBadge ? (
    <NodeProbe id={id} use={item.useBadge} report={report} />
  ) : null;
}

function DataProbe({
  id,
  use,
  report,
}: {
  id: string;
  use(): ShellBadge | null;
  report: BadgeReport;
}) {
  const badge = use();
  const key = JSON.stringify(badge);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for the badge
  useEffect(() => report(id, badge), [id, key, report]);
  useEffect(() => () => report(id, null), [id, report]);
  return null;
}

function NodeProbe({
  id,
  use,
  report,
}: {
  id: string;
  use(): ReactNode;
  report: BadgeReport;
}) {
  const node = use();
  const shown = node !== null && node !== undefined && node !== false;
  useEffect(
    () => report(id, shown ? { kind: "dot", tone: "accent", label: "" } : null),
    [id, shown, report],
  );
  useEffect(() => () => report(id, null), [id, report]);
  return null;
}
